import { useEffect, useMemo, useState } from 'react'
import { useRouter } from 'next/router'
import { useDispatch, useSelector } from 'react-redux'
import { useCookies } from 'react-cookie'
import {
  performSearchOffers as nltPerformSearchOffers,
  setCustomizedOffer as setNltCustomizedOffer,
} from 'src/redux/nlt-offer'
import {
  getAllOfferVariations,
  getOfferVariationsGrouped,
  getVariationIdFromCombination,
  getVariationPriceFromCombination,
  searchVariations,
} from 'src/redux/nlt-offer/selectors'
import { performSearchOffers as sellPerformSearchOffers } from 'src/redux/sell-offer'
import { getSearchParams } from 'src/utils/querystring'

export const useQuerystringToFilterOffers = (domain: 'nlt' | 'sell') => {
  const dispatch = useDispatch()
  const performSearchOffers = domain === 'nlt' ? nltPerformSearchOffers : sellPerformSearchOffers

  useEffect(() => {
    if (getSearchParams().size > 0) {
      dispatch(performSearchOffers())
    }
  }, [])
}

export const useBackButton = () => {
  const onPopState = () => {
    if (getSearchParams().size > 0) {
      location.reload()
    }
  }

  useEffect(() => {
    window.addEventListener('popstate', onPopState)

    return () => {
      window.removeEventListener('popstate', onPopState)
    }
  })
}

export const useTracking = () => {
  const { query } = useRouter()
  const [validQuery, setValidQuery] = useState<any>({})
  const [allCookies, setCookie, removeCookie] = useCookies(['CP_TRACKER'])
  const path = '/'
  const today = new Date()
  const validity = parseInt(process.env.NEXT_PUBLIC_TRACKING_COOKIE_VALIDITY)
  const expires = new Date(today.setDate(today.getDate() + validity))

  useEffect(() => {
    setValidQuery(query)
  }, [query])

  const trackingData = () => allCookies['CP_TRACKER']

  const setTracking = () => {
    const {
      utm_source: source = null,
      utm_medium: medium = null,
      utm_ref: referrer = null,
      utm_campaign: campaign = null,
      utm_campaign_date: campaign_date = null,
    } = { ...query, ...validQuery }

    if (
      (source || medium || referrer) &&
      (typeof allCookies['CP_TRACKER'] === 'undefined' || !allCookies['CP_TRACKER'])
    ) {
      setCookie(
        'CP_TRACKER',
        {
          source,
          referrer,
          medium,
          campaign,
          campaign_date,
        },
        { path, expires },
      )
    }
  }

  const removeTracking = () => removeCookie('CP_TRACKER')

  return [setTracking, removeTracking, trackingData]
}

export type CustomizedNltOfferParam = 'duration' | 'distance' | 'deposit'

export const useCustomizedNltOffer = (offerData: OfferGlobal.RentOfferDetail) => {
  const dispatch = useDispatch()
  const customizedOffer = useSelector((state: Store.RootState) => state.nlt.customizedOffer)

  const variations = useMemo(() => getAllOfferVariations(offerData), [offerData])

  const defaultOffer = {
    code: offerData.code,
    contractLength: offerData.contractLength,
    annualMileage: offerData.annualMileage,
    deposit: offerData.deposit,
    monthlyRate: offerData.monthlyRate,
    variationId: 0,
  }

  /* Ignoriamo la personalizzazione di un'altra offerta rimasta in store */
  const offer =
    customizedOffer && customizedOffer.code === offerData.code
      ? { ...defaultOffer, ...customizedOffer }
      : defaultOffer

  const getMonthlyRate = (contractLength: number, annualMileage: number, deposit: number) =>
    getVariationPriceFromCombination(variations, contractLength, annualMileage, deposit)

  const selectVariationItem = (param: CustomizedNltOfferParam, value: number) => {
    const selected = { ...offer }

    switch (param) {
      case 'duration':
        selected.contractLength = value
        break
      case 'distance':
        selected.annualMileage = value
        break
      case 'deposit':
        selected.deposit = value
        break
      default:
        return
    }

    const { contractLength, annualMileage, deposit } = selected

    dispatch(
      setNltCustomizedOffer({
        ...selected,
        monthlyRate: getMonthlyRate(contractLength, annualMileage, deposit),
        variationId: getVariationIdFromCombination(variations, contractLength, annualMileage, deposit),
      }),
    )
  }

  return { offer, variations, getMonthlyRate, selectVariationItem }
}

export const useSelectableDeposits = (offerData: OfferGlobal.RentOfferDetail) => {
  const { offer, getMonthlyRate, selectVariationItem, variations } = useCustomizedNltOffer(offerData)

  const { deposits } = getOfferVariationsGrouped(
    searchVariations(variations, offer.contractLength, offer.annualMileage, offer.deposit),
  )

  const monthlyRateFromDeposit = (deposit: number) => {
    const monthlyRate = getMonthlyRate(offer.contractLength, offer.annualMileage, deposit)

    return monthlyRate === null || typeof monthlyRate === 'undefined' ? null : monthlyRate
  }

  const selectableDeposits = deposits
    .filter((deposit: any) => deposit === offer.deposit || monthlyRateFromDeposit(deposit) !== null)
    .sort((depositA: any, depositB: any) => depositA - depositB)

  return {
    offer,
    selectableDeposits,
    hasDepositChoice: selectableDeposits.length > 1,
    monthlyRateFromDeposit,
    selectVariationItem,
  }
}
